import type { AppProps } from 'next/app';
import { Head } from '@zg-rentals/next/pages';
// This is needed until @zillow/page-frame updates to use constellation-10
import { ThemeConstellation } from '@zillow/constellation';
import { ThemeProvider } from 'styled-components';
import { ToastProvider } from '@zillow/constellation-10';
import { DefaultQueryProvider } from '@zg-rentals/react-query5/client';
import '@zillow/constellation-fonts/zillow-fonts.css';

import '../styles/panda.css';
import { ROUTES, SUPPORT_REQUEST_URL } from '../constants/urls';
import dynamic from 'next/dynamic';
import { RpErrorBoundary, useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { bootstrap } from '../bootstrap';
import { ErrorPage, ErrorPageContainer } from '@zg-rentals/visual-cohesion';
import { useRouter } from 'next/router';
import { setClient as setCappsClient } from '@zg-rentals/ts-capps-client/client';
import { setClient as setIdVerificationClient } from '@zg-rentals/ts-id-verification-client/client';
import { setClient as setRinsClient } from '@zg-rentals/ts-rins-client/client';
import { getClient } from '@zg-rentals/ts-client-http-client';
import { useComscorePageTracking } from '@zg-rentals/comscore/client';
import { trackComscoreEvent, trackEvent } from '../analytics';
import { useZrmPageViewEvents } from '@zg-rentals/clickstream';

const NoSsrApp = dynamic(() => import('../components/App'), { ssr: false });

setCappsClient(getClient().extend({ prefixUrl: process.env.NEXT_PUBLIC_RENTAL_PREFIX_URL }));
setIdVerificationClient(getClient().extend({ prefixUrl: process.env.NEXT_PUBLIC_RENTAL_PREFIX_URL }));
setRinsClient(getClient().extend({ prefixUrl: process.env.NEXT_PUBLIC_RENTAL_PREFIX_URL }));

const ApplicationsRenterWeb = (appProps: AppProps) => {
  useBootstrap(() => ({ bootstrap }));

  const router = useRouter();
  useComscorePageTracking(() => trackComscoreEvent({}), router.pathname);
  useZrmPageViewEvents(trackEvent, router.pathname);

  return (
    <>
      <Head applicationName="applications-renter-web" buildRef={process.env.NEXT_PUBLIC_BUILD_REF}>
        {/* oxlint-disable-next-line constellation-content/sentence-case */}
        <title>Rental Applications | Zillow</title>
      </Head>

      <RpErrorBoundary
        fallback={
          <ErrorPageContainer layout="header-nav">
            <ErrorPage
              errorMessage="Something went wrong on our end"
              errorDetails="Try refreshing the page. If that doesn’t work, check back in a few minutes."
              action={<a href={ROUTES.INDEX}>Go to applications</a>}
              secondaryAction={<a href={SUPPORT_REQUEST_URL}>Contact support</a>}
            />
          </ErrorPageContainer>
        }
      >
        <DefaultQueryProvider
          defaultOptions={{
            queries: {
              retry: false,
              refetchOnWindowFocus: false,
              staleTime: 1000 * 60 * 30, // 30 minutes
            },
          }}
          mutationErrorContext={() => undefined} // Note: disable serializing mutation variables
        >
          <ToastProvider duration={{ info: 3000, success: 3000 }}>
            <ThemeProvider theme={ThemeConstellation}>
              <NoSsrApp appProps={appProps} />
            </ThemeProvider>
          </ToastProvider>
        </DefaultQueryProvider>
      </RpErrorBoundary>
    </>
  );
};

export default ApplicationsRenterWeb;
